/**
 * The one place Studio binds a workspace-scoped rstore query to the runtime lifecycle (ADR 051).
 *
 * A definition may be `optional`, meaning it still loads during preparation but its failure does not
 * fail the switch; `refresh()` stays strict for every definition.
 *
 * Every query is `cache-first`: hydration is the one fetch, later reads and `use()` never fetch, and
 * `refresh()` reloads through the same query so rows the response no longer contains leave `data`
 * (query tracking is on because `createWorkspaceRStore` sets `experimentalGarbageCollection`).
 */
import type { VueQueryFetchState } from '@rstore/vue';
import type { ComputedRef } from 'vue';
import type { WorkspaceRuntimeManager } from '#layers/runtime/workspace/manager';
import type { WorkspaceRuntime, WorkspaceRuntimeStore } from '#layers/runtime/workspace/types';
import { computed } from 'vue';

/** What a definition may need beyond the store; every hydration receives the same context. */
export interface HydrationContext {
	userId: string;
}

/** The slice of an RStore query the workspace lifecycle owns. */
export type EntityQuery<TData = unknown> = PromiseLike<unknown> & {
	data: { value: TData };
	foreground: Pick<VueQueryFetchState, 'loading' | 'error'>;
	refresh: () => PromiseLike<unknown>;
};

export type EntityQueryData<TQuery extends EntityQuery> = TQuery['data']['value'];

/**
 * A `q.many` query resolves to an array; anything that is not an array (a `q.first` item or `null`)
 * is a singleton. The overloads of `defineWorkspaceHydration` key `empty` on that split.
 */
export type SingletonEntityQueryData = Record<string, unknown> | null;

export interface WorkspaceHydrationReader<TQuery extends EntityQuery> {
	/** The active runtime's query data, or `empty` when none is active. Never fetches. */
	data: ComputedRef<EntityQueryData<TQuery>>;
	/** Whether the active runtime's query is reloading; the initial load never shows because an unhydrated runtime is never active. */
	isRefreshing: ComputedRef<boolean>;
	/** Reloads the active runtime's query and replaces its data. Rejects with the fetch error. */
	refresh: () => Promise<void>;
}

export interface WorkspaceHydration<TQuery extends EntityQuery = EntityQuery> {
	/**
	 * Creates or joins the runtime's query and resolves with its loaded data; rejects when the load
	 * fails, unless the definition is `optional`, which resolves to `empty` instead.
	 */
	hydrate: (runtime: WorkspaceRuntime<WorkspaceRuntimeStore>, context: HydrationContext) => Promise<EntityQueryData<TQuery>>;
	/** Follows `workspaceRuntime.activeRuntime`. */
	use: () => WorkspaceHydrationReader<TQuery>;
}

const CACHE_FIRST = 'cache-first';

interface QueryBuilderOptions {
	fetchPolicy?: string;
}

interface CollectionQueryApi {
	query: (build: (builder: unknown) => QueryBuilderOptions) => unknown;
}

function isCollectionQueryApi(value: unknown): value is CollectionQueryApi {
	return typeof value === 'object' && value !== null && typeof (value as Partial<CollectionQueryApi>).query === 'function';
}

function pinCacheFirst(options: QueryBuilderOptions): QueryBuilderOptions {
	if (options.fetchPolicy !== undefined && options.fetchPolicy !== CACHE_FIRST) {
		throw new Error(`Workspace hydration queries are ${CACHE_FIRST}; the definition asked for "${options.fetchPolicy}"`);
	}

	return { ...options, fetchPolicy: CACHE_FIRST };
}

/** The store as the definition sees it: every collection's `query` pins the seam's fetch policy. */
function withCacheFirstQueries<TStore extends object>(store: TStore): TStore {
	return new Proxy(store, {
		get(target, key) {
			const value: unknown = Reflect.get(target, key);
			if (!isCollectionQueryApi(value)) return value;

			return Object.create(value, {
				query: { value: (build: (builder: unknown) => QueryBuilderOptions) => value.query((builder) => pinCacheFirst(build(builder))) },
			});
		},
	});
}

/** rstore parks a failed load on the query instead of rejecting; hydration and refresh must surface it. */
function throwQueryError(query: EntityQuery) {
	const error = query.foreground.error.value;
	if (error) throw error;
}

export function createWorkspaceHydrationDefiner<TStore extends WorkspaceRuntimeStore>(manager: WorkspaceRuntimeManager<TStore>) {
	type AnyRuntime = WorkspaceRuntime<WorkspaceRuntimeStore>;

	/**
	 * `empty` is what `use()` yields while no hydrated runtime is active; reads may run before any query
	 * exists, so the value is spelled at the definition and each overload checks it against the query's result.
	 */
	function defineWorkspaceHydration<TQuery extends EntityQuery<readonly unknown[]>>(
		create: (store: TStore, context: HydrationContext) => TQuery,
		options: { empty: []; optional?: boolean },
	): WorkspaceHydration<TQuery>;
	function defineWorkspaceHydration<TQuery extends EntityQuery<SingletonEntityQueryData>>(
		create: (store: TStore, context: HydrationContext) => TQuery,
		options: { empty: null; optional?: boolean },
	): WorkspaceHydration<TQuery>;

	function defineWorkspaceHydration<TQuery extends EntityQuery>(
		create: (store: TStore, context: HydrationContext) => TQuery,
		{ empty, optional = false }: { empty: [] | null; optional?: boolean },
	): WorkspaceHydration<TQuery> {
		const queries = new WeakMap<AnyRuntime, TQuery>();

		function activeQuery() {
			const runtime = manager.activeRuntime.value;
			return runtime ? queries.get(runtime) : undefined;
		}

		async function hydrate(runtime: AnyRuntime, context: HydrationContext): Promise<EntityQueryData<TQuery>> {
			let query = queries.get(runtime);

			if (!query) {
				const created = runtime.scope.active ? runtime.scope.run(() => create(withCacheFirstQueries(runtime.store as TStore), context)) : undefined;
				if (!created) throw new Error(`Workspace runtime "${runtime.slug}" is disposed`);
				query = created;
				queries.set(runtime, query);
			}

			await query;
			// An optional definition still loads during preparation, but its failure leaves the caller
			// with `empty` rather than failing the switch.
			if (!optional) throwQueryError(query);

			return (query.data.value ?? empty) as EntityQueryData<TQuery>;
		}

		async function refresh() {
			const query = activeQuery();
			if (!query) return;
			await query.refresh();
			throwQueryError(query);
		}

		function use(): WorkspaceHydrationReader<TQuery> {
			const data = computed(() => (activeQuery()?.data.value ?? empty) as EntityQueryData<TQuery>);
			const isRefreshing = computed(() => activeQuery()?.foreground.loading.value ?? false);

			return { data, isRefreshing, refresh };
		}

		return { hydrate, use };
	}

	return defineWorkspaceHydration;
}
